import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { updateEmployeeOptOutValue } from '../../../graphql/kudos/useKudosMutations'
import { showNotification } from '../Notification/notification'

interface CreateKudosModalProps {
  visible: boolean
  closeModal: () => void
  newOptOutValue: boolean
  onKudosOptOutValueUpdated: () => void
  employeeId: string
  employeeFullName: string
}

export const EmployeeKudosOptOutModal: FC<CreateKudosModalProps> = ({
  visible,
  closeModal,
  newOptOutValue,
  onKudosOptOutValueUpdated,
  employeeId,
  employeeFullName,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleUpdate = async () => {
    setIsLoading(true)
    setErrorMessage('')

    const result = await updateEmployeeOptOutValue(employeeId, newOptOutValue)

    if (!!result.data && !result.errors) {
      onKudosOptOutValueUpdated()
      resetErrorAndCloseModal()
      showNotification({
        type: 'success',
        message: !newOptOutValue
          ? t('kudos.notifications.updateOptOutValue.enableSuccessTitle')
          : t('kudos.notifications.updateOptOutValue.disableSuccessTitle'),
        description: !newOptOutValue
          ? t('kudos.notifications.updateOptOutValue.enableSuccessDescriptionPublicProfile', { employee: employeeFullName })
          : t('kudos.notifications.updateOptOutValue.disableSuccessDescriptionPublicProfile', { employee: employeeFullName }),
      })
    } else {
      const errorMessage = result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.updateOptOutModal.error')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetErrorAndCloseModal = () => {
    setErrorMessage('')
    setIsLoading(false)
    closeModal()
  }

  return (
    <PerModal
      title={{ heading: !newOptOutValue ? t('kudos.updateOptOutModal.enableTitle') : t('kudos.updateOptOutModal.disableTitle') }}
      centered
      visible={visible}
      onCancel={resetErrorAndCloseModal}
      footer={{
        cancelBtn: {
          text: t('global.button.cancel'),
          onClick: resetErrorAndCloseModal,
          icon: 'close_stroke',
          visible: !isLoading,
        },
        confirmBtn: {
          altText: t('kudos.updateOptOutModal.confirm'),
          onClick: handleUpdate,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <PerFlexRow direction="column">
        {newOptOutValue ? (
          <>
            <PerText fontSize="m" fontWeight="bold" mb="xxs">
              {t('kudos.profileSection.optOut.modalOptOutDescriptionPublicProfile1', { employee: employeeFullName })}
            </PerText>
            <PerText>{t('kudos.profileSection.optOut.modalOptOutDescriptionPublicProfile2', { employee: employeeFullName })}</PerText>
          </>
        ) : (
          <>
            <PerText fontSize="m" fontWeight="bold" mb="xxs">
              {t('kudos.profileSection.optOut.modalOptInDescriptionPublicProfile1', { employee: employeeFullName })}
            </PerText>
            <PerText>{t('kudos.profileSection.optOut.modalOptInDescriptionPublicProfile2', { employee: employeeFullName })}</PerText>
          </>
        )}

        {errorMessage && (
          <PerText color="error" fontSize="m" mt="m">
            {errorMessage}
          </PerText>
        )}
      </PerFlexRow>
    </PerModal>
  )
}

export default EmployeeKudosOptOutModal
